import { Fragment, ReactNode, useCallback, useMemo } from "react";

import {
    BsMetaUtil,
    useOraclePrices,
    useAllAvailablePrices,
    useLiveClock,
    LOAN_TICK,
    useUserTokens,
    UserPointsFunction,
    UserVaultPositionExpanded,
    UserVaultPnl,
    isDisabledValue
} from "@bridgesplit/abf-react";
import {
    Autocomplete,
    BORDER_RADIUS,
    BORDER_RADIUS_ROUNDED,
    Button,
    ButtonProps,
    Column,
    FONT_SIZES,
    FormInput,
    FormNumberInputProps,
    INPUT_HEIGHT,
    Icon,
    IconButton,
    Image,
    InputWrapper,
    LOGO_URL,
    MEDIA,
    MediaQuery,
    PairValue,
    PopoverWrapper,
    Row,
    SPACING,
    Select,
    Span,
    StatColumn,
    StatContainer,
    StatRow,
    SxType,
    Text,
    TextButton,
    TextColor,
    TextProps,
    TextSkeleton,
    TextVariant,
    ToggleButtons,
    TooltipText,
    UsePopoverResult,
    repeatElement,
    useAppPalette,
    useElementDimensions,
    useMemoizedKeyMap,
    useResetWithCoolDown
} from "@bridgesplit/ui";
import {
    SortDirection,
    TIME,
    formatPercent,
    formatUsd,
    bsMath,
    formatNum,
    MayNumber,
    roundDownToDecimals,
    DISABLED_APY_BIGINT,
    formatToken,
    safeBigIntToNumber,
    tokenAmountToUiNumber,
    formatOrDash
} from "@bridgesplit/utils";
import { ExpandMore, FilterAltOutlined, NorthOutlined, Refresh, SouthOutlined } from "@mui/icons-material";
import { TokenListMetadata } from "@bridgesplit/abf-sdk";
import { Box, Skeleton } from "@mui/material";
import { AppTab } from "app/constants";
import { AppDialog, AppDialogData, useAppDialog } from "app/utils";

import { TokenImage, TokenSize } from "./asset";
import { SocialShareCta, useVaultSocialShareProps } from "../portfolio";
import { BalanceDisplay } from "./wallet";

export function getTabPath(path: string, tab: AppTab) {
    return `${path}?open=${tab}`;
}

interface TokenInputWithPriceProps {
    value: number | undefined;
    setValue: (n: number | undefined) => void;
    metadata: TokenListMetadata | undefined;
    belowInput?: ReactNode;
    disabled?: boolean;
    disableTokenSelect?: boolean;
    label?: string;
    selectToken: AppDialogData<AppDialog.SelectToken> | (() => void) | null;
    loading?: boolean;
    autoFocus?: boolean;
    labelColor?: TextColor;
    formNumberInputProps?: Omit<FormNumberInputProps, "value" | "setValue" | "variant">;
    supportedDepositTokens?: TokenListMetadata[] | undefined;
    hideUsdPrice?: boolean;
    wrapperSx?: SxType;
    tokenImageSize?: TokenSize;
}
export function TokenInputWithPrice({
    value,
    setValue,
    metadata,
    loading,
    disabled,
    label,
    selectToken,
    autoFocus,
    labelColor = "caption",
    disableTokenSelect,
    belowInput,
    formNumberInputProps,
    supportedDepositTokens,
    hideUsdPrice,
    wrapperSx,
    tokenImageSize = "sm"
}: TokenInputWithPriceProps) {
    const { getOracle } = useOraclePrices([metadata?.mint]);

    const usdPrice = getOracle(metadata?.mint)?.getUsdAmount(value, metadata?.decimals);

    return (
        <Column spacing={0.5}>
            {!!label && (
                <Text variant="body2" color={labelColor}>
                    {label}
                </Text>
            )}
            <InputWrapper height={SPACING * 6.5} sx={{ justifyContent: "space-between", ...wrapperSx }}>
                <TokenSwitch
                    metadata={metadata}
                    selectToken={selectToken}
                    disableTokenSelect={disableTokenSelect}
                    supportedDepositTokens={supportedDepositTokens}
                    tokenImageSize={tokenImageSize}
                />
                {loading ? (
                    <Skeleton sx={{ height: FONT_SIZES.h1, width: "70px", justifyContent: "flex-end" }} />
                ) : (
                    <Column sx={{ width: "100%", justifyContent: "flex-end", textAlign: "right", overflow: "hidden" }}>
                        <FormInput
                            autoFocus={autoFocus}
                            onClick={(e) => e.stopPropagation()}
                            inputProps={{
                                sx: {
                                    padding: 0,
                                    textAlign: "right",
                                    border: "none",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis"
                                }
                            }}
                            fullWidth
                            sx={{
                                height: SPACING * 3,
                                "& fieldset": { border: "none" },
                                "& .MuiInputBase-root": { overflow: "hidden" }
                            }}
                            variant="number"
                            decimals={metadata?.decimals}
                            disabled={disabled}
                            setValue={setValue}
                            placeholder="0"
                            value={value}
                            {...formNumberInputProps}
                        />
                        {!!usdPrice && !hideUsdPrice && (
                            <Text sx={{ justifyContent: "flex-end" }} variant="caption" color="caption" translate="no">
                                {formatUsd(usdPrice)}
                            </Text>
                        )}
                    </Column>
                )}
            </InputWrapper>
            {belowInput}
        </Column>
    );
}

interface TokenInputProps extends Omit<FormNumberInputProps, "variant" | "label"> {
    maxAmount: MayNumber;
    maxText?: string;
    symbol?: string;
    label?: string;
    showBalance?: boolean;
    maxLoading?: boolean;
    setMax?: (n: number | undefined) => void; // override setValue (ex for non debounced input)
    metadata: TokenListMetadata | undefined;
    hideRefresh?: boolean;
}
export function TokenInput({
    maxAmount,
    placeholder,
    maxText,
    symbol,
    label,
    showBalance = true,
    setMax,
    maxLoading,
    metadata,
    hideRefresh,
    ...props
}: TokenInputProps) {
    const roundedMaxAmount = roundDownToDecimals(maxAmount ?? 0, metadata?.decimals ?? 0);
    const isMaxSelected = !!maxAmount && props.value === roundedMaxAmount;
    return (
        <FormInput
            label={label}
            decimals={metadata?.decimals}
            belowInputElement={
                showBalance ? (
                    <BalanceDisplay
                        balance={maxAmount ?? 0}
                        metadata={metadata}
                        maxText={maxText}
                        hideRefresh={hideRefresh}
                        insufficientBalance={false}
                        isLoading={maxLoading ?? false}
                        setMax={() => {
                            if (maxLoading) return;
                            const newAmount = isMaxSelected ? undefined : roundedMaxAmount;
                            if (setMax) {
                                setMax(newAmount);
                            } else {
                                props.setValue(newAmount);
                            }
                        }}
                        maxButtonCta={isMaxSelected ? "Clear" : undefined} // already defaults to Max
                    />
                ) : undefined
            }
            adornment={props.adornment ?? symbol}
            variant="number"
            placeholder={placeholder ?? "0"}
            {...props}
        />
    );
}

export function NullTokenInput({ onClick, loading }: { onClick?: () => void; loading?: boolean }) {
    return (
        <Column spacing={0.5}>
            <Text variant="body2">Your collateral</Text>
            <InputWrapper height={SPACING * 6.5} sx={{ justifyContent: "space-between" }}>
                <NullTokenSwitch onClick={onClick} loading={loading} />
            </InputWrapper>
        </Column>
    );
}
export function NullTokenSwitch({ onClick, loading }: { onClick?: () => void; loading?: boolean }) {
    return (
        <Button
            disableRipple
            width="max-content"
            height={30}
            textProps={{ variant: "body2" }}
            sx={{ px: 1.5, borderRadius: BORDER_RADIUS_ROUNDED }}
            variant="outlined"
            onClick={(e) => {
                // prevents double click
                if (e.detail === 0) return;
                if (!onClick) return;
                onClick();
            }}
            disabled={loading}
        >
            <Image skeletonVariant="circular" variant="circle" size={FONT_SIZES.body1 + "px"} src={LOGO_URL} />
            <Span sx={{ width: "max-content", alignItems: "center", display: "flex" }}>
                {loading ? <TextSkeleton variant="body1" width="80px" /> : "Select a token"} <ExpandMore />
            </Span>
        </Button>
    );
}

function TokenSwitch({
    metadata,
    selectToken,
    disableTokenSelect,
    supportedDepositTokens,
    tokenImageSize = "sm"
}: Pick<
    TokenInputWithPriceProps,
    "metadata" | "selectToken" | "disableTokenSelect" | "supportedDepositTokens" | "tokenImageSize"
>) {
    const { open } = useAppDialog();
    const { tokens: userTokens } = useUserTokens();
    const balanceByMint = useMemo(() => {
        const balances = new Map<string, number>();
        userTokens?.forEach((token) => {
            balances.set(token.mint, (balances.get(token.mint) ?? 0) + (token.tokenAmount.uiAmount ?? 0));
        });
        return balances;
    }, [userTokens]);
    const showBalances = typeof selectToken === "object" && !!selectToken?.showBalances;

    const getBalanceLabel = useCallback(
        (token: TokenListMetadata | undefined) => {
            if (!showBalances || !token) return null;
            return (
                <Text variant="caption" color="caption" translate="no">
                    {BsMetaUtil.formatAmount(token, balanceByMint.get(token.mint) ?? 0)}
                </Text>
            );
        },
        [balanceByMint, showBalances]
    );

    if (selectToken === null) {
        return (
            <Row sx={{ minWidth: "max-content" }} spacing={1}>
                <TokenImage metadata={metadata} size={tokenImageSize} />
                <Text loading={!metadata} loadingWidth="30px">
                    {BsMetaUtil.getSymbol(metadata)}
                </Text>
            </Row>
        );
    }

    // If we have tokens array and it's small (≤ 3), use dropdown (only show other tokens, not current)
    if (supportedDepositTokens && supportedDepositTokens.length <= 3 && typeof selectToken === "object") {
        const otherTokens = supportedDepositTokens.filter((token) => token.mint !== metadata?.mint);
        const options = otherTokens.map((token) => ({
            value: token.mint,
            label: (
                <Row spacing={1} sx={{ width: "100%" }} spaceBetween>
                    <Row spacing={1}>
                        <TokenImage metadata={token} size="sm" />
                        <Text>{BsMetaUtil.getSymbol(token)}</Text>
                    </Row>
                    {getBalanceLabel(token)}
                </Row>
            )
        }));
        const selectValue = options.some((option) => option.value === metadata?.mint) ? metadata?.mint : "";

        if (otherTokens.length > 0) {
            return (
                <Select
                    sx={{
                        minWidth: "max-content",
                        width: "max-content",
                        height: 30,
                        borderRadius: BORDER_RADIUS_ROUNDED, // outlined border inherits radius from the root
                        "& .MuiSelect-select": {
                            px: 1.5,
                            py: 0.5,
                            borderRadius: BORDER_RADIUS_ROUNDED,
                            display: "flex",
                            alignItems: "center",
                            gap: 1
                        }
                    }}
                    hideIcon={true}
                    disabled={disableTokenSelect}
                    value={selectValue}
                    setValue={(mint) => {
                        const selectedToken = supportedDepositTokens.find((t) => t.mint === mint);
                        if (selectedToken) {
                            selectToken.setToken(selectedToken);
                        }
                    }}
                    options={options}
                    renderValue={() => (
                        <Row spacing={1} sx={{ alignItems: "center" }}>
                            <TokenImage metadata={metadata} size="sm" />
                            <Text variant="body2">
                                {metadata ? (
                                    BsMetaUtil.getSymbol(metadata)
                                ) : (
                                    <TextSkeleton variant="body1" width="30px" />
                                )}
                            </Text>
                            {getBalanceLabel(metadata)}
                            <ExpandMore />
                        </Row>
                    )}
                />
            );
        }
    }

    // Fallback to original button behavior for dialog or function calls
    return (
        <Button
            disableRipple
            width="max-content"
            height={30}
            textProps={{ variant: "body2" }}
            sx={{ px: 1.5, borderRadius: BORDER_RADIUS_ROUNDED }}
            variant="outlined"
            onClick={(e) => {
                // prevents double click
                if (e.detail === 0) return;
                if (disableTokenSelect) return;
                if (typeof selectToken === "object") {
                    return open(AppDialog.SelectToken, selectToken);
                }
                selectToken();
            }}
        >
            <TokenImage metadata={metadata} size="sm" />
            <Span sx={{ width: "max-content", alignItems: "center", display: "flex" }}>
                {metadata ? BsMetaUtil.getSymbol(metadata) : <TextSkeleton variant="body1" width="30px" />}{" "}
                <ExpandMore />
            </Span>
        </Button>
    );
}

export function getSamplingInterval(valueInSeconds: number): number {
    switch (valueInSeconds) {
        case TIME.DAY:
            return TIME.HOUR;
        case TIME.WEEK:
            return TIME.HOUR * 4;
        case TIME.MONTH:
            return TIME.HOUR * 12;
        case TIME.MONTH * 3:
            return TIME.DAY;
        default:
            throw new Error("Invalid value for sampling interval");
    }
}

export function VaultProfitLoss({
    vaultPosition,
    userVaultPnl,
    variant
}: {
    vaultPosition: UserVaultPositionExpanded | undefined;
    userVaultPnl: UserVaultPnl | undefined;
    variant?: TextVariant;
}) {
    const socialShareData = useVaultSocialShareProps(vaultPosition, userVaultPnl);

    if ((userVaultPnl?.vaultPnl || 0) < 0) {
        return (
            <TooltipText
                helpText="Interest earned will show after some has been accrued"
                variant={variant ?? "body1"}
                color="disabled"
            >
                Pending
            </TooltipText>
        );
    }

    return (
        <Column alignItems="flex-start" spacing={0.5}>
            <Row spacing={0.5}>
                <ProfitLossText
                    fontWeight="normal"
                    variant={variant}
                    collateralPnl={userVaultPnl?.vaultPnl}
                    metadata={vaultPosition?.vaultExpanded?.principalMetadata}
                />
                {socialShareData && <SocialShareCta data={socialShareData} />}
            </Row>
            {vaultPosition?.pnlUsd !== undefined && (
                <Text variant="body2" color="caption">
                    {formatUsd(vaultPosition.pnlUsd)}
                </Text>
            )}
        </Column>
    );
}
export function ProfitLossText({
    variant,
    arrowSize = 12,
    fontWeight,
    metadata,
    collateralPnl,
    usdPnl,
    condensedOnMobile,
    percentChange,
    query = MEDIA.SM,
    percentPosition = "left"
}: {
    arrowSize?: number;
    metadata?: TokenListMetadata;
    collateralPnl?: number | undefined;
    usdPnl?: number;
    condensedOnMobile?: boolean;
    percentChange?: number;
    query?: MediaQuery;
    percentPosition?: "left" | "right";
} & Pick<TextProps, "variant" | "fontWeight">) {
    const { error, success, textDisabled } = useAppPalette();

    const pnl = collateralPnl ?? usdPnl ?? 0;
    const isLoading = collateralPnl === undefined && usdPnl === undefined;

    const { icon, color } = useMemo(() => {
        if (pnl > 0) return { icon: <NorthOutlined />, color: success };
        if (pnl < 0) return { icon: <SouthOutlined />, color: error };
        return { icon: null, color: textDisabled };
    }, [error, pnl, success, textDisabled]);

    function PercentComponent() {
        return (
            <Text
                variant="caption"
                sx={{
                    gap: 0.5,
                    color,
                    svg: { fontSize: `${arrowSize}px` }
                }}
            >
                {icon}
                {percentChange !== undefined && formatPercent(percentChange)}
            </Text>
        );
    }

    function TokenAmountComponent() {
        if (!metadata) return null;
        const tokenAmount = collateralPnl ? Math.abs(collateralPnl) : collateralPnl;
        return (
            <Text loading={isLoading} variant={variant} fontWeight={fontWeight} translate="no">
                {BsMetaUtil.formatAmount(metadata, tokenAmount)}
            </Text>
        );
    }

    function UsdComponent() {
        if (usdPnl === undefined) return null;
        return (
            <Text
                variant={variant}
                sx={
                    condensedOnMobile && collateralPnl !== undefined
                        ? { [query.below]: { display: "none" } }
                        : undefined
                }
                color={collateralPnl !== undefined ? "caption" : "body"}
                translate="no"
            >
                {formatUsd(Math.abs(usdPnl))}
            </Text>
        );
    }

    return (
        <>
            {percentPosition === "left" && !!pnl && <PercentComponent />}
            <TokenAmountComponent />
            <UsdComponent />
            {percentPosition === "right" && !!pnl && <PercentComponent />}
        </>
    );
}

export function FilterButtonPopover({
    children,
    popoverProps,
    reset,
    icon
}: {
    children: ReactNode;
    popoverProps: UsePopoverResult;
    reset: () => void;
    icon?: ReactNode;
}) {
    return (
        <>
            <PopoverWrapper transformOrigin={{ vertical: -1 * SPACING, horizontal: SPACING * 2 }} {...popoverProps}>
                <Column sx={{ minWidth: "300px", p: 2 }} spacing={2}>
                    <Row spaceBetween>
                        <Text>Filters</Text>
                        <TextButton onClick={popoverProps.close}>
                            <Icon type="reject" />
                        </TextButton>
                    </Row>
                    {children}
                    <TextButton color="caption" onClick={reset}>
                        Clear filters
                    </TextButton>
                </Column>
            </PopoverWrapper>

            <IconButton
                sx={{ borderRadius: BORDER_RADIUS, height: INPUT_HEIGHT, width: INPUT_HEIGHT }}
                onClick={popoverProps.open}
                textVariant="h4"
                textColor="caption"
                jsxIcon={icon ?? <FilterAltOutlined />}
            />
        </>
    );
}

export function PastActiveToggle({
    value,
    setValue,
    query = MEDIA.LG
}: {
    value: boolean;
    setValue: (b: boolean) => void;
    query?: MediaQuery;
}) {
    return (
        <ToggleButtons
            containerSx={{ [query.above]: { width: "200px" } }}
            value={value ? 1 : 0}
            setValue={(val) => setValue(!!val)}
            options={[
                { value: 1, label: "Ongoing" },
                { value: 0, label: "Past" }
            ]}
        />
    );
}

export function SortDirectionButton({
    direction,
    setDirection
}: {
    direction: SortDirection | undefined;
    setDirection: (sortSide: SortDirection) => void;
}) {
    return (
        <IconButton
            sx={{ borderRadius: BORDER_RADIUS }}
            onClick={() => setDirection(direction === SortDirection.Asc ? SortDirection.Desc : SortDirection.Asc)}
            textVariant="h4"
            textColor="caption"
            jsxIcon={
                <SouthOutlined
                    sx={{
                        transform: direction === SortDirection.Asc ? "rotate(-180deg)" : undefined,
                        transition: "300ms ease-in-out"
                    }}
                />
            }
        />
    );
}

export function RefreshButton({
    refresh,
    disabled = false,
    textColor = "caption",
    textVariant = "caption",
    background = "hover",
    border = true
}: {
    refresh: () => void;
    disabled?: boolean;
    textColor?: TextColor;
    textVariant?: TextVariant;
    background?: "always" | "hover" | "never";
    border?: boolean;
}) {
    const resetWithCoolDown = useResetWithCoolDown();
    return (
        <IconButton
            textVariant={textVariant}
            textColor={textColor}
            disabled={disabled}
            border={border}
            background={background}
            sx={{ p: 0.5 }}
            jsxIcon={<Refresh />}
            onClick={() => resetWithCoolDown(refresh)}
        />
    );
}

export function SettingsButton({ children, sx, ...props }: Omit<ButtonProps, "textProps">) {
    const { textDisabled } = useAppPalette();
    return (
        <Button
            height={22}
            variant="outlined"
            sx={{ p: 1.5, borderRadius: BORDER_RADIUS_ROUNDED, ...sx }}
            textProps={{
                variant: "body2",
                sx: { gap: 1, svg: { color: textDisabled, fontSize: "inherit" } }
            }}
            {...props}
        >
            {children}
        </Button>
    );
}

export function PositionCardStats({
    variant = "body1",
    forceColumn,
    ...props
}: StatContainer & { forceColumn?: boolean }) {
    const [ref, { width }] = useElementDimensions();
    const isRow = width > 800 || forceColumn;

    return (
        <Box ref={ref}>
            {isRow ? <StatRow variant={variant} {...props} /> : <StatColumn variant={variant} {...props} />}
        </Box>
    );
}

export function TagsDisplay({
    tags,
    dotSize = 3,
    variant = "body2"
}: {
    tags: string[] | string | undefined;
    dotSize?: number;
    variant?: TextVariant;
}) {
    const tagsArray = useMemo(() => {
        if (tags === undefined || !tags?.length) return [];
        if (Array.isArray(tags)) return tags;
        const tagsParsed = JSON.parse(tags);
        if (Array.isArray(tagsParsed) && tagsParsed.every((tag) => typeof tag === "string")) return tagsParsed;
        return [];
    }, [tags]);

    return (
        <Row spacing={0.5}>
            {tags === undefined && repeatElement(<TextSkeleton variant={variant} width="20px" />)}
            {tagsArray.map((tag, i) => (
                <Fragment key={tag}>
                    {i !== 0 && (
                        <Box
                            sx={{
                                backgroundColor: (theme) => theme.palette.text.disabled,
                                width: dotSize,
                                height: dotSize,
                                borderRadius: "100%"
                            }}
                        />
                    )}
                    <Text variant={variant} color="caption">
                        {tag}
                    </Text>
                </Fragment>
            ))}
        </Row>
    );
}

export function TokenAutoSelect({
    mint,
    setMint,
    tokens,
    placeholder,
    disableClearable,
    sx
}: {
    mint: string | undefined;
    setMint: (mint: string | undefined) => void;
    tokens: TokenListMetadata[] | undefined;
    placeholder?: string;
    disableClearable?: boolean;
    sx?: SxType;
}) {
    const getKey = useCallback((t: TokenListMetadata) => t.mint, []);
    const mintToMetadata = useMemoizedKeyMap(tokens ?? [], getKey);
    const selectedToken = mintToMetadata?.get(mint ?? "");
    return (
        <Autocomplete
            getOptionLabel={(t) => BsMetaUtil.getSymbol(mintToMetadata?.get(t))}
            InputProps={{
                startAdornment: selectedToken ? <TokenImage metadata={selectedToken} size="sm" /> : undefined
            }}
            sx={{ minWidth: "200px", ...sx }}
            renderOption={(mint) => {
                const token = mintToMetadata?.get(mint);
                if (!token) return <>None</>;
                return (
                    <Row spacing={0.5}>
                        <TokenImage metadata={token} size="sm" />
                        {BsMetaUtil.getNameOrSymbol(token)}
                    </Row>
                );
            }}
            disableClearable={disableClearable}
            placeholder={placeholder ?? "Search by token"}
            options={tokens?.map(getKey)}
            value={mint}
            setValue={setMint}
        />
    );
}

export function StackedCurrencyDisplay({
    metadata,
    amount,
    alignItems,
    topTextVariant = "body1",
    bottomTextVariant = "body2",
    spacing = 0.5,
    loading,
    reverse = false
}: {
    alignItems: "flex-start" | "flex-end";
    metadata: TokenListMetadata | undefined;
    amount: number | undefined;
    topTextVariant?: TextVariant;
    bottomTextVariant?: TextVariant;
    spacing?: number;
    loading?: boolean;
    reverse?: boolean;
}) {
    const { getPriceByMint } = useAllAvailablePrices(metadata?.mint ? [metadata?.mint] : undefined);
    const tokenDisplay = BsMetaUtil.formatAmount(metadata, amount, {
        hideSymbol: false
    });
    const price = getPriceByMint(metadata?.mint);
    const usdDisplay = formatUsd(price !== undefined && amount !== undefined ? bsMath.mul(price, amount) : undefined);

    return (
        <Column alignItems={alignItems} spacing={spacing}>
            <Text color="body" loading={loading} variant={topTextVariant} translate="no">
                {reverse ? usdDisplay : tokenDisplay}
            </Text>
            <Text variant={bottomTextVariant} loading={loading} color="caption" translate="no">
                {reverse ? tokenDisplay : usdDisplay}
            </Text>
        </Column>
    );
}

export function NeighboringCurrencyDisplay({
    metadata,
    amount,
    principalVariant = "body1"
}: {
    metadata: TokenListMetadata | undefined;
    amount: number | undefined;
    principalVariant?: TextVariant;
}) {
    const { getPriceByMint } = useAllAvailablePrices(metadata?.mint ? [metadata?.mint] : undefined);

    return (
        <PairValue
            value={[
                BsMetaUtil.formatAmount(metadata, amount, { hideSymbol: false }),
                formatUsd(bsMath.mul(getPriceByMint(metadata?.mint), amount))
            ]}
            variant={principalVariant}
            secondaryVariant="body2"
        />
    );
}

export function NeighboringCurrencyDecimalDisplay({
    metadata,
    amount,
    principalVariant = "body1"
}: {
    metadata: TokenListMetadata | undefined;
    amount: bigint | undefined;
    principalVariant?: TextVariant;
}) {
    const { getPriceByMint } = useAllAvailablePrices(metadata?.mint ? [metadata?.mint] : undefined);

    const tokenAmount =
        amount === undefined || metadata?.decimals === undefined ? undefined : { amount, decimals: metadata.decimals };
    const uiAmount = tokenAmountToUiNumber(tokenAmount);

    return (
        <Row spacing={1}>
            <Text color="body" variant={principalVariant}>
                {formatToken(tokenAmount)}
            </Text>
            <Text variant="body2" color="caption">
                {isDisabledValue(amount) ? "N/A" : formatUsd(bsMath.mul(getPriceByMint(metadata?.mint), uiAmount))}
            </Text>
        </Row>
    );
}

export function NeighboringPercentageDisplay({
    metadata,
    amount,
    principalVariant = "body1",
    amountMaximum,
    nullText = "Disabled"
}: {
    metadata: TokenListMetadata | undefined;
    amount: number;
    amountMaximum: bigint | undefined;
    principalVariant?: TextVariant;
    nullText?: string;
}) {
    if (amountMaximum === undefined || amountMaximum === DISABLED_APY_BIGINT || !metadata?.decimals) {
        return (
            <Row>
                <Text color="caption">{nullText}</Text>
            </Row>
        );
    }

    return (
        <Row spacing={1}>
            <Text color="body" variant={principalVariant}>
                {formatOrDash([amountMaximum, metadata?.decimals], () =>
                    formatToken({ amount: amountMaximum, decimals: metadata.decimals }, { symbol: metadata?.symbol })
                )}
            </Text>
            <Text variant="body2" color="caption">
                {formatPercent(amount / safeBigIntToNumber(amountMaximum))}
            </Text>
        </Row>
    );
}

export function Points({ getPoints }: { getPoints: UserPointsFunction }) {
    const now = useLiveClock(LOAN_TICK);
    const data = getPoints(now);

    return <> {formatNum(data?.totalPoints ?? 0)} </>;
}

export function PercentAndUsdRow({
    percent,
    usdValue,
    textVariant,
    percentColor
}: {
    percent: number | undefined;
    usdValue: number | undefined;
    textVariant: TextVariant;
    percentColor?: TextColor;
}) {
    if (percent === undefined || usdValue === undefined) return null;

    return (
        <Row spacing={0.5}>
            <Text variant={textVariant} color={percentColor}>
                {formatPercent(percent)}
            </Text>
            <Text variant={textVariant} color="caption">
                {formatUsd(usdValue)}
            </Text>
        </Row>
    );
}

export function MaxButton({
    onClick,
    isSelected,
    children,
    disabled,
    textSx
}: {
    onClick: () => void;
    isSelected: boolean;
    children: React.ReactNode;
    disabled: boolean;
    textSx?: SxType;
}) {
    const { textDisabled, divider, textPrimary, textSecondary } = useAppPalette();
    return (
        <Button
            disabled={disabled}
            onClick={onClick}
            sx={{
                border: `0.5px ${isSelected ? textDisabled : divider} solid`,
                px: 1,
                borderRadius: BORDER_RADIUS_ROUNDED
            }}
            width="min-content"
            variant="outlined"
            height={20}
            textProps={{ variant: "caption", sx: { color: isSelected ? textPrimary : textSecondary, ...textSx } }}
        >
            {children}
        </Button>
    );
}
